import React from 'react';
import { Volume2, VolumeX } from 'lucide-react';
import {
    angleIndexOf,
    clipVolume,
    formatTimeLabel,
    hasCustomVolume,
    insetSoundFrom,
    isClipMuted,
    isMulticam,
} from '../state/timelineModel';

/**
 * One clip on a track.
 *
 * Positioned absolutely in pixels off the track's left edge, so its width is
 * its real duration at the current zoom and a trim handle is always the same
 * physical size regardless of how long the edit is.
 */

/* A clip narrower than its own handles cannot show them without covering itself
   entirely; below that width it is a sliver you move but do not trim. Three
   handles wide leaves a third of the clip as body to grab for a move.

   The multiplier matters more on touch than it looks: handles go from 9px to
   22px under `(pointer: coarse)`, so the same clip that comfortably showed
   handles under a mouse would be nothing but handles under a finger. */
const MIN_TRIMMABLE_HANDLES = 3;

/* Whose sound an inset plays with, readable along the track without selecting
   each one. */
const INSET_SOUND_BADGES = {
    main: { badge: <VolumeX size={12} aria-hidden="true" />, title: 'Sound: main video' },
    inset: { badge: <><Volume2 size={12} aria-hidden="true" /> only</>, title: 'Sound: this overlay only' },
    both: { badge: <><Volume2 size={12} aria-hidden="true" />+</>, title: 'Sound: overlay mixed with the main video' },
};
const FALLBACK_HANDLE_WIDTH = 9;

function Clip({
    clip,
    kind,
    selected,
    editable,
    secondsToPx,
    handleWidth,
    onSelect,
    onBeginMove,
    onBeginTrim,
    cutAt = null,
}) {
    const left = secondsToPx(clip.start);
    const width = Math.max(2, secondsToPx(clip.duration));
    const label = clip.label || clip.asset_filename || 'Clip';
    // Falls back to the fine-pointer width for the first paint, before the
    // viewport has been measured.
    const handle = handleWidth > 0 ? handleWidth : FALLBACK_HANDLE_WIDTH;
    const showHandles = editable && width > handle * MIN_TRIMMABLE_HANDLES;
    /* A silenced clip looks exactly like a loud one, and the inspector only
       shows the selected clip — so without a mark on the clip itself, "why is
       this section silent" is unanswerable from the timeline. */
    const insetSound = kind === 'overlay' ? INSET_SOUND_BADGES[insetSoundFrom(clip)] : null;
    const muted = insetSound ? insetSoundFrom(clip) === 'main' : isClipMuted(clip);
    const levelBadge = insetSound
        ? insetSound.badge
        : (muted ? <VolumeX size={12} aria-hidden="true" /> : (hasCustomVolume(clip) ? `${Math.round(clipVolume(clip) * 100)}%` : ''));
    const levelTitle = insetSound ? insetSound.title : (muted ? 'Muted' : `Volume ${levelBadge}`);
    /* Which camera this piece is cut to. Consecutive segments of one take look
       identical otherwise, and reading a multicam sequence means seeing where
       it changes camera without clicking through every piece. */
    const cameraBadge = isMulticam(clip) ? `C${angleIndexOf(clip) + 1}` : '';
    /* An inset cannot outlive the video it sits on: the render ends with the
       main sequence. The part past that is marked, not hidden, so it is seen
       and trimmed rather than discovered missing from the export. */
    const overhang = cutAt !== null && clip.end > cutAt
        ? Math.min(width, secondsToPx(clip.end - Math.max(cutAt, clip.start)))
        : 0;

    return (
        <div
            className={`nle-clip nle-clip-${kind} ${selected ? 'selected' : ''}`}
            style={{ left, width }}
            onPointerDown={(event) => {
                onSelect(clip.id);
                if (editable) onBeginMove(event, clip);
            }}
            title={`${label} — ${formatTimeLabel(clip.duration)}${
                clip.sourceIn > 0 ? ` (from ${formatTimeLabel(clip.sourceIn)} in the source)` : ''
            }`}
            role="button"
            tabIndex={0}
            aria-label={`${label}, ${formatTimeLabel(clip.duration)}${
                cameraBadge ? `, camera ${angleIndexOf(clip) + 1}` : ''
            }${muted ? ', muted' : ''}`}
            aria-pressed={selected}
        >
            {showHandles && (
                <span
                    className="nle-clip-handle left"
                    onPointerDown={(event) => onBeginTrim(event, clip, 'trim-left')}
                    aria-hidden="true"
                />
            )}

            <span className="nle-clip-body">
                <span className="nle-clip-label">
                    {cameraBadge && <span className="nle-clip-camera">{cameraBadge}</span>}
                    {label}
                </span>
                {levelBadge && (
                    <span className="nle-clip-level" title={levelTitle}>
                        {levelBadge}
                    </span>
                )}
                {width > 90 && (
                    <span className="nle-clip-meta">{formatTimeLabel(clip.duration)}</span>
                )}
            </span>

            {overhang > 0 && (
                <span
                    className="nle-clip-overhang"
                    style={{ width: overhang }}
                    title="Past the end of the main video — this part will not render"
                />
            )}

            {showHandles && (
                <span
                    className="nle-clip-handle right"
                    onPointerDown={(event) => onBeginTrim(event, clip, 'trim-right')}
                    aria-hidden="true"
                />
            )}
        </div>
    );
}

export default React.memo(Clip);
